/**
 * RegenOne — inspector-scoped "regenerate this section" modal.
 *
 * Spec 23 P10.5 + Spec 22 regen-producer MVP. The writer opens this
 * from the Inspector header to capture a regen intent for one
 * section, with optional free-text guidance ("e.g. cite the
 * updated SAP; use per-protocol set not ITT"). Confirm dispatches
 * a ``regen`` EventLog entry — the Spec 22 MVP producer — which
 * ticks the ``regens`` counter used by the ⭐ badge + event legend.
 *
 * SRP: presentation only. The shell owns the actual re-run
 * workflow (re-setting the selection picker with the target module
 * pre-checked, or a future in-place re-run once the state machine
 * supports it). This component captures the writer's decision +
 * guidance text; the shell routes it.
 *
 * # DECISION: single-option MVP — no "include downstream" radio.
 * # WHY: the scenes prototype's "§9.3 + downstream" radio
 * #      hardcodes "§2 · §11 · §12" for CSR; that's doctype-
 * #      specific invention. Real downstream data requires
 * #      per-module dep tracking that neither the planner nor the
 * #      runtime expose today. Rather than faking the list, we
 * #      ship a single-section regen now and grow the radio group
 * #      cleanly when we have a backend signal to populate it.
 * # DATE: 2026-04-22
 *
 * # DECISION: confirm is an EventLog dispatch; the actual re-run
 * #   is the shell's concern.
 * # WHY: Spec 23 §5 Q5 — backend regen signal is future-scope. The
 * #      shell's handler may route the intent to a reset + pre-
 * #      seeded LoomSelection (reusing the existing pipeline), or
 * #      to a future direct re-run when that lands. The modal
 * #      stays honest about WHAT was captured (decision + text)
 * #      without committing to HOW it re-runs.
 * # DATE: 2026-04-22
 */

import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Dialog, LinkButton, Overlay, Tag, TextField } from "../../ds/components";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RegenTarget {
  /** Section id for the EventLog entry + the eventual re-run's
   *  ``module_ids``. */
  sectionId: string;
  /** Human-readable section number for display ("9.3"). */
  sectionNumber: string;
  /** Human-readable title — "Primary endpoint analysis". */
  sectionTitle: string;
  /** Gate name ("assemble" / "configure" / …) for colour + context. */
  gateName: string;
  /** Display label for the gate — "Assemble". */
  gateDisplayName: string;
  /** Gate's palette index (position in config.gates) so the modal
   *  uses the same colour the spine uses for this gate. */
  gatePaletteIndex: number;
  /** Rough estimate — "~8m" drives the "this will take" copy.
   *  Derived from ``estMinutes(node)`` by the caller. */
  estMinutes: number;
  /** Source library the redraft reads from, shown as a scope tag. */
  libraryName?: string | null;
}

export interface RegenConfirmation {
  sectionId: string;
  /** Writer's free-text guidance. Null when empty — the shell
   *  shouldn't ship an empty string as the prompt. */
  guidance: string | null;
}

export interface RegenOneProps {
  target: RegenTarget;
  /** Writer clicked Regenerate — shell dispatches the regen event +
   *  routes to the re-run workflow. */
  onConfirm: (confirmation: RegenConfirmation) => void;
  /** Writer clicked Cancel or × — just close. */
  onCancel: () => void;
  /** Offer "copy it word for word from a source instead" — opens the copy
   *  picker for this section (no model). */
  onCopyInstead?: () => void;
}

export function formatRegenButton(target: RegenTarget): string {
  return `Regenerate · ~${target.estMinutes} min`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RegenOne: React.FC<RegenOneProps> = ({ target, onConfirm, onCancel, onCopyInstead }) => {
  const [guidance, setGuidance] = useState<string>("");

  const handleConfirm = useCallback(() => {
    const trimmed = guidance.trim();
    onConfirm({
      sectionId: target.sectionId,
      guidance: trimmed.length > 0 ? trimmed : null,
    });
  }, [guidance, onConfirm, target.sectionId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  // Design board 15. The pane-wide layer keeps the dialog above the spine and
  // inspector wherever the shell renders it.
  return (
    <div className="as-regen-layer">
      <Overlay placement="top" onDismiss={onCancel}>
        <Dialog
          title={`Regenerate §${target.sectionNumber}?`}
          description="This section is redrafted from scratch. The current draft stays in Word until the new one arrives, then the new draft replaces it at the section's heading."
          confirmLabel={formatRegenButton(target)}
          onCancel={onCancel}
          onConfirm={handleConfirm}
        >
          <div className="as-tags">
            <Tag>{target.sectionNumber} only</Tag>
            {target.libraryName ? <Tag>{target.libraryName}</Tag> : null}
          </div>
          {onCopyInstead ? (
            <p className="as-note">
              Or take §{target.sectionNumber} word for word from a source document, such as the previous version. You
              review it before Word changes.{" "}
              <LinkButton onClick={onCopyInstead}>Copy from a source instead</LinkButton>
            </p>
          ) : null}
          <TextField
            label="What should change? (optional)"
            multiline
            value={guidance}
            onChange={setGuidance}
            placeholder="e.g. Cite the updated SAP. Use the per-protocol set, not ITT."
            help="Leave empty to redraft with the same instructions."
          />
        </Dialog>
      </Overlay>
    </div>
  );
};

export default RegenOne;
